import history from 'libs/history';
import type { RcFile } from 'antd/es/upload';

/**
 * 将对象转换为可以在 Select 组件中使用的 options 数组。
 * @param {Object} obj 具有键值对的对象
 * @returns {Array} 一系列选项
 */
export const object2Options = (obj: { [key: string]: string }) => {
  return Object.entries(obj).map(([key, value]) => {
    return {
      label: value,
      value: key
    };
  });
};

/**
 * 安全的 JSON.parse 实现
 * @param text 要解析的 JSON 字符串
 * @param reviver 转换函数
 * @returns 解析后的对象，或 undefined（如果解析失败）
 */
export function safeJsonParse<T = unknown>(
  text: unknown,
  reviver?: Parameters<JSON['parse']>[1]
): T | undefined {
  try {
    // 先验证是否是有效的 JSON 字符串
    if (typeof text !== 'string' || !text.trim()) {
      return undefined;
    }

    // 处理潜在的污染攻击
    const sanitizedText = text
      // eslint-disable-next-line no-control-regex
      .replace(/^\s*[\u0000-\u001F]+/, '') // 移除开头的控制字符
      .trim();

    return JSON.parse(sanitizedText, reviver);
  } catch (error) {
    console.error('JSON.parse error:', error);
    return undefined;
  }
}

/**
 * 下载文件
 * @param url 文件 URL
 * @param filename 文件名，留空则默认为 'download'
 */
export function downloadFile(url: string, filename = 'download') {
  const a = document.createElement('a');

  // 如果是同源URL或blob/data URL
  if (
    url.startsWith('blob:') ||
    url.startsWith('data:') ||
    new URL(url, window.location.href).origin === window.location.origin
  ) {
    a.href = url;
    a.download = filename; // 同源时 filename 有效
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else {
    // 跨域资源处理方案
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      })
      .catch(() => {
        // 最终降级方案
        window.open(url, '_blank');
      });
  }
}

/**
 * 剔除所有空字符串，null 和 undefined 的属性
 * @param obj 要处理的对象
 * @param options 配置选项
 * @param options.deep 是否深度处理嵌套对象，默认为 false
 * @returns 处理后的新对象
 */
export function xTrim(obj: Record<string, any>, options: { deep?: boolean } = {}) {
  // 如果传入的不是对象或者是 null，直接返回
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  const result: Record<string, any> = {};

  // 遍历对象的所有属性
  for (const [key, value] of Object.entries(obj)) {
    // 检查值是否为空（空字符串、null、undefined）
    if (value !== '' && value !== null && value !== undefined) {
      // 如果开启了深度处理且值是对象，递归处理
      if (options.deep && typeof value === 'object' && value !== null) {
        result[key] = xTrim(value, options);
      } else {
        result[key] = value;
      }
    }
  }

  return result;
}

export const formatFileSizeCustom = (bytes: number, decimals = 1) => {
  if (!Number.isFinite(bytes)) return '0 B';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getImageFileSize: (
  file: RcFile
) => Promise<{ width: number; height: number }> = async (file: RcFile) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };
      img.onerror = () => {
        resolve({
          width: 0,
          height: 0
        });
      };
    };
  });
};

/**
 * 清除时间戳的毫秒
 * @param ts 时间戳
 * @returns
 */
export const clearMilliseconds = (ts: number) => {
  if (typeof ts !== 'number') return ts;
  return Math.floor(ts / 1000) * 1000;
};

/** 解析hash地址，并且提取相关参数 */
export const parseHashRoute = (url?: string) => {
  const fullHash = url || window.location.hash || '#/';
  const hash = fullHash.substring(1) || '/';
  const [path, queryString = ''] = hash.split('?');

  const params = new URLSearchParams(queryString);
  const query = {};
  for (const [key, value] of params.entries()) {
    // @ts-ignore
    query[key] = value;
  }

  // 提取路径参数（例如：/products/:id）
  const segments = path.split('/').filter(Boolean);

  return {
    fullHash,
    hash,
    path: path || '/',
    segments,
    query,
    queryString
  };
};

/**
 * 根据指定字段组合成一个对象，支持嵌套
 * @example
 * const obj = {
    fieldCode: 'bottomNav_learnCenter',
    languageText: '哈哈哈'
};
const obj2 = arrayToNestedObject(obj, {name: 'fieldCode',value: 'languageText', split: '_'})
则obj2的结果如下
{
    bottomNav: {
        learnCenter: '哈哈哈'
    }
}
 * 
 */
export const arrayToNestedObject = (
  arr: any[],
  fileds?: { name: string; value: string; split: string }
) => {
  const { name, value, split } = fileds || {
    name: 'fieldCode',
    value: 'languageText',
    split: '_'
  };
  const result = {};
  arr.forEach(item => {
    const keys = item[name].split(split);
    const val = item[value];
    let current = result as any;

    // 遍历所有键（除了最后一个）
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!current[key] || typeof current[key] !== 'object') {
        // 如果当前键不存在或不是对象，创建新对象
        current[key] = {};
      }
      current = current[key]; // 深入下一层
    }

    // 设置最后一个键的值
    const lastKey = keys[keys.length - 1];
    current[lastKey] = val;
  });

  return result;
};
